import type { OHLCV } from '@shared/types/candle'

import { registerIndicator } from '@/indicators/registry'
import type { IndicatorConfig, IndicatorResult } from '@/types/indicator'
import { IndicatorBase } from '@/types/indicator'

const CONFIG: IndicatorConfig = {
  id: 'mfi',
  name: 'Money Flow Index',
  shortName: 'MFI',
  category: 'momentum',
  overlay: false,
  params: [{ key: 'period', label: 'Period', type: 'number', default: 14, min: 2, max: 200 }],
  lines: [{ key: 'mfi', label: 'MFI', color: '#7b1fa2', width: 1 }],
  priceRange: { min: 0, max: 100 },
  priceLevels: [80, 20],
}

class MFI extends IndicatorBase {
  readonly config = CONFIG

  calculate(candles: OHLCV[], params: Record<string, number | string | boolean>): IndicatorResult {
    const period = Math.max(2, Number(params['period']) || 14)
    const n = candles.length
    const values: (number | null)[] = new Array<number | null>(n).fill(null)

    if (n <= period) return { lines: [{ key: 'mfi', values }] }

    // Typical price and raw money flow for each bar
    const tp: number[] = candles.map((c) => (c.h + c.l + c.c) / 3)
    const rmf: number[] = candles.map((c, i) => tp[i] * c.v)

    for (let i = period; i < n; i++) {
      let posMF = 0
      let negMF = 0
      for (let j = i - period + 1; j <= i; j++) {
        // Money flow direction based on TP vs previous TP
        if (tp[j] > tp[j - 1]) posMF += rmf[j]
        else negMF += rmf[j]
        // Equal TP counts as neither (no contribution)
      }
      if (negMF === 0) {
        values[i] = 100
      } else {
        const mfr = posMF / negMF
        values[i] = 100 - 100 / (1 + mfr)
      }
    }

    return { lines: [{ key: 'mfi', values }] }
  }
}

registerIndicator(MFI, CONFIG)
